import { PlusIcon } from '@phosphor-icons/react';
import { Formik } from 'components/form/formik';
import diff from 'object-diff';
import { useMemo } from 'react';
import toast from 'react-hot-toast';
import { COUNTRY_VALUES } from '@utils/address';
import * as Yup from 'yup';

import { Button } from '../../../../components/button/Button';
import { CheckboxField } from '../../../../components/checkbox/CheckboxField';
import { InputField } from '../../../../components/input/InputField';
import { SimpleSelectField } from '../../../../components/select/SimpleSelectField';
import { TextAreaField } from '../../../../components/textarea/TextAreaField';
import { Country, Language } from 'src/types/enums';
import { updateLocation } from '../../models';
import { getDisplayError } from '../../../../utils/get-display-error';
import { LANGUAGE_VALUES } from '../../../../utils/language';
import { useLocation } from './locationContext';
import { AutocompletePostalcode } from '../../components/AutocompletePostalcode';
import { Card } from 'components/Card';
import { captureException } from '@sentry/react';

const updateLocationSchema = Yup.object().shape({
  name: Yup.string().min(1, 'Vereist').required('Vereist'),
  street: Yup.string().min(1, 'Vereist').required('Vereist'),
  streetNumber: Yup.string(),
  city: Yup.string().min(1, 'Vereist').required('Vereist'),
  postalCode: Yup.string().min(1, 'Vereist').required('Vereist'),
  country: Yup.mixed().nullable().required('Vereist'),
  language: Yup.mixed().nullable().required('Vereist'),
});

export const UpdateLocationForm = () => {
  const { location, refreshData } = useLocation();

  const initialValues = useMemo(() => {
    return {
      name: location.name,
      street: location.street,
      streetNumber: location.streetNumber,
      city: location.city,
      postalCode: location.postalCode,
      country: COUNTRY_VALUES.find((v) => v.key === location.country)!,
      language: LANGUAGE_VALUES.find((v) => v.key === location.language)!,
      internalNotes: location.internalNotes,
      externalNotes: location.externalNotes,
      isExternalBooking: location.isExternalBooking,
    };
  }, [location]);

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={updateLocationSchema}
        onSubmit={async (newValues) => {
          try {
            const patch: Partial<typeof newValues> = diff(initialValues, newValues);
            const updateData = {
              ...patch,
              country: patch.country ? (patch.country.key as Country) : undefined,
              language: patch.language ? (patch.language.key as Language) : undefined,
            };
            if (Object.values(updateData).length) {
              await updateLocation(location.id, updateData);
              refreshData();
            }
            toast.success('Locatie aangepast');
          } catch (err: any) {
            captureException(err);
            toast.error('Kon locatie niet aanpassen: ' + getDisplayError(err));
          }
        }}
      >
        {({ handleSubmit, isSubmitting, errors }) => {
          return (
            <form onSubmit={handleSubmit} className="grid gap-4">
              <Card title="Algemene informatie">
                <InputField labelText="Naam" type="text" name="name" />
                <div className="flex gap-4">
                  <div className="flex-4">
                    <InputField labelText="Straat" type="text" name="street" />
                  </div>
                  <div className="flex-1">
                    <InputField labelText="Nr" type="text" name="streetNumber" />
                  </div>
                </div>

                <AutocompletePostalcode />

                <SimpleSelectField labelText="Taal" items={LANGUAGE_VALUES} name="language" />
                <CheckboxField labelText="Externe boeking" name="isExternalBooking" />
              </Card>

              <Card title="Notities">
                <TextAreaField labelText="Interne notities" name="internalNotes" spellCheck={true} />
                <TextAreaField labelText="Externe notities (voor vervoerder)" name="externalNotes" spellCheck={true} />
              </Card>

              <div>
                <Button
                  type="submit"
                  color="primary"
                  iconLeft={<PlusIcon className="button-icon" />}
                  isDisabled={isSubmitting}
                  isLoading={isSubmitting}
                >
                  Pas locatie aan
                </Button>
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
};
